import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import {
  ArrowBack as ArrowBackIcon,
  ArrowDownward as ArrowDownwardIcon,
  ArrowForward as ArrowForwardIcon,
  ArrowUpward as ArrowUpwardIcon,
  DeleteOutline as DeleteOutlineIcon,
  OpenWith as OpenWithIcon,
  RotateRight as RotateRightIcon,
  Train as TrainIcon,
} from '@mui/icons-material';
import { View3D } from './View3D';
import {
  createInitialSlotsState,
  parseSlotKey,
  getSlotKey,
  type BlockMap,
  type SlotsState,
  getTopTier,
  getContainersInSlot,
  getSlotDisplayName,
} from './slots';
import { colors } from '../../const';
import { IconButton } from '../button';
import { Checkbox } from '../checkbox';
import { Paper } from '../paper';
import { enqueueSnackbar } from '../snackbar';
import type { ContainerType } from '../../types';
import {
  createSlotsStateFromBlocks,
  getOrderedSlotKey,
  type YardPlaceSceneBlock,
  type YardPlaceSceneContainer,
} from './scene-state';
import { YARD_PLACE_SCENE_MESSAGES } from './validate-container-place';

export type { YardPlaceSceneBlock, YardPlaceSceneContainer } from './scene-state';
export { createSlotsStateFromBlocks } from './scene-state';

export type YardPlaceSceneItem = {
  type: 'railroad-tracks' | 'boat' | 'text-background' | 'road';
  position?: { x: number; y: number };
  rotate?: number;
  text?: string;
  width?: number;
  height?: number;
  length?: number;
  arrows?: boolean;
};

export type YardPlaceSceneData = {
  blocks?: YardPlaceSceneBlock[];
  containers?: YardPlaceSceneContainer[];
  items?: YardPlaceSceneItem[];
};

const getItemsWithDefaultPositions = (
  items: YardPlaceSceneItem[],
  blocks: YardPlaceSceneBlock[],
) => {
  let nextColumn = 0;
  let minRow = 0;
  let maxColumn = 0;

  blocks.forEach((block, index) => {
    const position = block.position;
    const startColumn = position?.x ?? nextColumn;
    const startRow = position?.y ?? 0;

    if (index === 0) minRow = startRow;
    minRow = Math.min(minRow, startRow);
    maxColumn = Math.max(maxColumn, startColumn + block.slotsLength - 1);
    nextColumn = Math.max(
      nextColumn,
      startColumn + Math.max(block.slotsLength, block.rowsLength) + 2,
    );
  });

  return items.map((item, index) => ({
    ...item,
    position: item.position ?? { x: maxColumn + 3 + index * 3, y: minRow },
  }));
};

const getSceneCenterPosition = (blocks: YardPlaceSceneBlock[]) => {
  if (!blocks.length) return { x: 0, y: 0 };

  let nextColumn = 0;
  let minColumn = Infinity;
  let maxColumn = -Infinity;
  let minRow = Infinity;
  let maxRow = -Infinity;

  blocks.forEach((block) => {
    const position = block.position;
    const startColumn = position?.x ?? nextColumn;
    const startRow = position?.y ?? 0;

    minColumn = Math.min(minColumn, startColumn);
    maxColumn = Math.max(maxColumn, startColumn + block.slotsLength - 1);
    minRow = Math.min(minRow, startRow);
    maxRow = Math.max(maxRow, startRow + block.rowsLength - 1);
    nextColumn = Math.max(
      nextColumn,
      startColumn + Math.max(block.slotsLength, block.rowsLength) + 2,
    );
  });

  return {
    x: (minColumn + maxColumn) / 2,
    y: (minRow + maxRow) / 2,
  };
};

type AddedContainer = {
  id: string;
  number: string;
  length: number;
};

export type YardPlaceSceneContainerPlace = {
  block: string;
  row: number;
  slot: number;
  tier: number;
};

export type YardPlaceSceneSlotHoverInfo = {
  slotKey: string;
  place: string;
  tier: number;
  x: number;
  y: number;
};

export type YardPlaceSceneContainerHoverInfo = {
  containerId: string;
  number: string;
  type?: ContainerType;
  clientCode?: string;
  place: string;
  tier: number;
  x: number;
  y: number;
};

export type YardPlaceSceneProps = {
  defaultValue?: string | YardPlaceSceneData;
  /** Цвет свободных слотов */
  slotColor?: string;
  /** Цвет занятых слотов */
  slotContainerColor?: string;
  /** Цвет выбранного контейнера */
  containerSelectedColor?: string;
  onChange?: (value: string) => void;
  /** Автоматически пересчитать tier контейнеров один раз при инициализации (не в edit-blocks) */
  autoTier?: boolean;
  defaultMode?: 'edit-blocks' | 'edit-containers' | 'view-containers';
  defaultSelected?: string;
  selectedContainerFocusKey?: string;
  selectedContainerId?: string;
  onSelectedContainerChange?: (containerId?: string) => void;
  renderOnEmptyClick?: (
    slotKey?: string,
    onClose?: () => void,
    onAddContainer?: (container: AddedContainer) => void,
    sceneContainers?: { id: string; number: string }[],
  ) => ReactNode;
  renderOnContainerClick?: (
    containerId?: string,
    onClose?: () => void,
    onDeleteContainer?: () => void,
  ) => ReactNode;
  renderOnSlotHover?: (info?: YardPlaceSceneSlotHoverInfo) => ReactNode;
  renderOnContainerHover?: (info?: YardPlaceSceneContainerHoverInfo) => ReactNode;
  onDeleteContainer?: (containerId: string, rollback: () => void) => void;
  onPasteContainer?: (
    containerId: string,
    place: YardPlaceSceneContainerPlace,
    rollback: () => void,
  ) => void;
  backgroundColor?: string;
};

const Layout = styled.div`
  position: relative;
  height: 100%;
`;

const SceneControls = styled.div`
  position: absolute;
  bottom: 1.6rem;
  left: 50%;
  display: flex;
  gap: 1.2rem;
  z-index: 1;
  transform: translateX(-50%);
`;

const AddItemsPaper = styled(Paper)`
  position: absolute;
  top: var(--spacing-4);
  left: var(--spacing-4);
  z-index: 1;
  display: flex;
  gap: var(--spacing-2);
  padding: var(--spacing-2) !important;
`;

const RoadCheckboxesPaper = styled(AddItemsPaper)`
  top: calc((var(--spacing-4) * 2) + 5.6rem);
  display: grid;
  grid-template-columns: repeat(2, max-content);
  gap: var(--spacing-2);
  width: max-content;
  height: auto;
  overflow: hidden !important;
`;

const RoadCheckbox = styled(Checkbox)`
  &.MuiCheckbox-root {
    display: grid;
    width: 4rem;
    height: 4rem;
    margin: 0;
    padding: 0.6rem;
    place-items: center;

    svg {
      font-size: 2.4rem;
    }
  }
`;

const RotateControl = styled.button`
  display: grid;
  width: 5.2rem;
  height: 5.2rem;
  place-items: center;
  border: 0;
  border-radius: 50%;
  background: var(--primary-100);
  color: var(--white);
  cursor: ew-resize;
  touch-action: none;
  box-shadow: 0 0.4rem 1.2rem rgb(0 0 0 / 25%);

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  svg {
    font-size: 3rem;
  }
`;

const RoadControl = styled(RotateControl)`
  cursor: pointer;
`;

const DeleteItemControl = styled(IconButton)`
  &.MuiIconButton-root {
    width: 5.2rem;
    height: 5.2rem;
    border-radius: 50%;
    background: var(--red-100);
    color: var(--white);
    box-shadow: 0 0.4rem 1.2rem rgb(0 0 0 / 25%);
  }

  &:hover {
    background: var(--red-80) !important;
  }
`;

const POSITION_DRAG_SENSITIVITY = 0.05;

const BlockRotateControl = ({
  disabled,
  onRotate,
}: {
  disabled: boolean;
  onRotate: (degrees: number) => void;
}) => {
  const controlRef = useRef<HTMLButtonElement>(null);
  const isDraggingRef = useRef(false);
  const onRotateRef = useRef(onRotate);

  onRotateRef.current = onRotate;

  useEffect(() => {
    const handleMouseMove = (event: MouseEvent) => {
      if (!isDraggingRef.current || !event.movementX) return;
      onRotateRef.current(event.movementX);
    };

    const handlePointerLockChange = () => {
      if (document.pointerLockElement !== controlRef.current) {
        isDraggingRef.current = false;
      }
    };

    const handleMouseUp = () => {
      isDraggingRef.current = false;
      if (document.pointerLockElement === controlRef.current) {
        document.exitPointerLock();
      }
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('pointerlockchange', handlePointerLockChange);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('pointerlockchange', handlePointerLockChange);

      if (document.pointerLockElement === controlRef.current) {
        document.exitPointerLock();
      }
    };
  }, []);

  const handleMouseDown = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (disabled) return;

    isDraggingRef.current = true;
    controlRef.current?.requestPointerLock();
  };

  return (
    <RotateControl
      ref={controlRef}
      type="button"
      aria-label="Rotate selected block"
      disabled={disabled}
      onMouseDown={handleMouseDown}
    >
      <RotateRightIcon />
    </RotateControl>
  );
};

const BlockPositionControl = ({
  disabled,
  onMove,
}: {
  disabled: boolean;
  onMove: (x: number, y: number) => void;
}) => {
  const controlRef = useRef<HTMLButtonElement>(null);
  const isDraggingRef = useRef(false);
  const onMoveRef = useRef(onMove);

  onMoveRef.current = onMove;

  useEffect(() => {
    const handleMouseMove = (event: MouseEvent) => {
      if (!isDraggingRef.current || (!event.movementX && !event.movementY)) return;

      onMoveRef.current(
        event.movementX * POSITION_DRAG_SENSITIVITY,
        event.movementY * POSITION_DRAG_SENSITIVITY,
      );
    };

    const handlePointerLockChange = () => {
      if (document.pointerLockElement !== controlRef.current) {
        isDraggingRef.current = false;
      }
    };

    const handleMouseUp = () => {
      isDraggingRef.current = false;
      if (document.pointerLockElement === controlRef.current) {
        document.exitPointerLock();
      }
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('pointerlockchange', handlePointerLockChange);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('pointerlockchange', handlePointerLockChange);

      if (document.pointerLockElement === controlRef.current) {
        document.exitPointerLock();
      }
    };
  }, []);

  const handleMouseDown = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (disabled) return;

    isDraggingRef.current = true;
    controlRef.current?.requestPointerLock();
  };

  return (
    <RoadControl
      ref={controlRef}
      type="button"
      aria-label="Move selected block"
      disabled={disabled}
      onMouseDown={handleMouseDown}
    >
      <OpenWithIcon />
    </RoadControl>
  );
};

const getBlocksFromDefaultValue = (
  defaultValue: string | YardPlaceSceneData,
): YardPlaceSceneBlock[] | undefined => {
  try {
    const parsed =
      typeof defaultValue === 'string'
        ? defaultValue
          ? JSON.parse(defaultValue)
          : {}
        : defaultValue;

    if (!Array.isArray(parsed?.blocks)) return undefined;

    let nextColumn = 0;
    return parsed.blocks.map((block: YardPlaceSceneBlock) => {
      const currentPosition = block.position;
      const position = currentPosition ?? { x: nextColumn, y: 0 };

      nextColumn = Math.max(
        nextColumn,
        position.x + Math.max(block.rowsLength, block.slotsLength) + 2,
      );

      return {
        ...block,
        position,
      };
    });
  } catch {
    return undefined;
  }
};

const getContainersFromDefaultValue = (
  defaultValue: string | YardPlaceSceneData,
): YardPlaceSceneContainer[] => {
  try {
    const parsed =
      typeof defaultValue === 'string'
        ? defaultValue
          ? JSON.parse(defaultValue)
          : {}
        : defaultValue;

    return Array.isArray(parsed?.containers) ? parsed.containers : [];
  } catch {
    return [];
  }
};

const getBlockPosition = (blocks: YardPlaceSceneBlock[], index: number) => {
  let nextColumn = 0;

  for (let currentIndex = 0; currentIndex <= index; currentIndex++) {
    const block = blocks[currentIndex];
    const position = block.position;
    const x = position?.x ?? nextColumn;
    const y = position?.y ?? 0;

    if (currentIndex === index) {
      return { x, y };
    }

    nextColumn = Math.max(nextColumn, x + Math.max(block.rowsLength, block.slotsLength) + 2);
  }

  return { x: 0, y: 0 };
};

const normalizeContainerTiers = (
  containers: SlotsState['containers'],
): SlotsState['containers'] => {
  const topTierBySlot = new Map<string, number>();
  const normalizedTiers = new Map<string, number>();

  containers
    .map((container, index) => ({ container, index }))
    .sort((a, b) => a.container.tier - b.container.tier || a.index - b.index)
    .forEach(({ container }) => {
      const slotKeys = container.slotKeys ?? [container.slotKey];
      const tier = Math.max(...slotKeys.map((slotKey) => topTierBySlot.get(slotKey) ?? 0)) + 1;

      normalizedTiers.set(container.id, tier);
      slotKeys.forEach((slotKey) => topTierBySlot.set(slotKey, tier));
    });

  return containers.map((container) => ({
    ...container,
    tier: normalizedTiers.get(container.id) ?? container.tier,
  }));
};

const slotKeyToContainerKeyPart = (slotKey: string, blockMaps: BlockMap[]): string | undefined => {
  const { col, row } = parseSlotKey(slotKey);

  for (const blockMap of blockMaps) {
    const { startCol, startRow, colsCount, rowsCount, id } = blockMap;

    if (
      col >= startCol &&
      col < startCol + colsCount &&
      row >= startRow &&
      row < startRow + rowsCount
    ) {
      const blockName = id.replace(/-\d+$/, '');

      return `${blockName}-${Math.round(row - startRow + 1)}-${Math.round(col - startCol + 1)}`;
    }
  }

  return undefined;
};

const containersToSceneContainers = (
  containers: SlotsState['containers'],
  blockMaps?: BlockMap[],
): YardPlaceSceneContainer[] =>
  containers.map((container) => {
    const slotKeys = container.slotKeys ?? [container.slotKey];
    const key = blockMaps
      ? slotKeys
          .map((slotKey) => slotKeyToContainerKeyPart(slotKey, blockMaps))
          .filter(Boolean)
          .join(',')
      : '';

    return {
      id: container.id,
      number: container.number,
      tier: container.tier,
      length: container.length,
      ...(container.type ? { type: container.type } : {}),
      ...(container.client ? { client: container.client } : {}),
      ...(key ? { key } : {}),
    };
  });

const getSceneDataValue = (
  blocks: YardPlaceSceneBlock[],
  containers: SlotsState['containers'],
  items: YardPlaceSceneItem[],
  blockMaps?: BlockMap[],
) =>
  JSON.stringify({
    blocks,
    containers: containersToSceneContainers(containers, blockMaps),
    items,
  });

const useInitialSlotsState = (defaultValue: string | YardPlaceSceneData): SlotsState => {
  return useMemo(() => {
    try {
      const parsed =
        typeof defaultValue === 'string'
          ? defaultValue
            ? (JSON.parse(defaultValue) as any)
            : {}
          : defaultValue;

      if (Array.isArray(parsed?.blocks)) {
        return createSlotsStateFromBlocks(parsed.blocks, parsed.containers);
      }

      const hasLegacySlots =
        Array.isArray(parsed?.slots) ||
        (Array.isArray(parsed?.containers) &&
          parsed.containers.some((c: any) => typeof c?.key === 'string'));

      if (hasLegacySlots) {
        const slots: string[] | undefined = parsed.slots;
        const containersProp: YardPlaceSceneContainer[] | undefined = parsed.containers;

        const hasSlots = !!slots && slots.length > 0;
        const hasContainers = !!containersProp && containersProp.length > 0;

        if (!hasSlots && !hasContainers) {
          return createInitialSlotsState();
        }

        const slotNames: NonNullable<SlotsState['slotNames']> = {};
        const containers: SlotsState['containers'] = [];

        if (hasSlots) {
          for (const label of slots) {
            const key = getOrderedSlotKey(label);
            slotNames[key] = label;
          }
        }

        if (hasContainers) {
          for (const item of containersProp) {
            if (!item.key) continue;
            const slotKey = getOrderedSlotKey(item.key);

            containers.push({
              id: item.id,
              slotKey,
              number: item.number,
              tier: item.tier ? Number(item.tier) : getTopTier(containers, slotKey),
              ...(item.type ? { type: item.type } : {}),
              ...(item.client ? { client: item.client } : {}),
            });

            if (!slotNames[slotKey]) {
              slotNames[slotKey] = item.key;
            }
          }
        }

        return {
          containers,
          slotNames,
        };
      }

      // Вариант 2: передан уже готовый SlotsState-подобный объект
      const { containers = [], slotNames = {} }: SlotsState = parsed || {};

      const orderedSlotNames = Object.entries(slotNames as Record<string, string>).reduce(
        (result, [, name]) => {
          result[getOrderedSlotKey(name)] = name;
          return result;
        },
        {} as Record<string, string>,
      );

      const orderedSlotKeysBySourceKey = Object.entries(slotNames as Record<string, string>).reduce(
        (result, [key, name]) => {
          result[key] = getOrderedSlotKey(name);
          return result;
        },
        {} as Record<string, string>,
      );

      const normalizedContainers = containers.map((c) => {
        const slotKey = orderedSlotKeysBySourceKey[c.slotKey] ?? c.slotKey;

        return {
          ...c,
          slotKey,
          tier: c.tier ? Number(c.tier) : getTopTier(containers, c.slotKey),
        };
      });

      return {
        containers: normalizedContainers,
        slotNames: orderedSlotNames,
      } as SlotsState;
    } catch {
      return createInitialSlotsState();
    }
  }, [defaultValue]);
};

export const YardPlaceScene: React.FC<YardPlaceSceneProps> = ({
  defaultValue = '{}',
  slotColor = colors['--gray-50'],
  slotContainerColor = colors['--primary-100'],
  containerSelectedColor = colors['--green-100'],
  defaultSelected = '',
  selectedContainerFocusKey,
  selectedContainerId: selectedContainerIdProp,
  onSelectedContainerChange,
  onChange,
  defaultMode = 'edit-containers',
  autoTier = defaultMode !== 'edit-blocks',
  renderOnEmptyClick,
  renderOnContainerClick,
  renderOnSlotHover,
  renderOnContainerHover,
  onDeleteContainer,
  onPasteContainer,
  backgroundColor = colors['--gray-60'],
}: YardPlaceSceneProps) => {
  const defaultBlocks = useMemo(() => getBlocksFromDefaultValue(defaultValue), [defaultValue]);
  const defaultContainers = useMemo(
    () => getContainersFromDefaultValue(defaultValue),
    [defaultValue],
  );
  const defaultItems = useMemo<YardPlaceSceneItem[]>(() => {
    try {
      const parsed =
        typeof defaultValue === 'string' ? JSON.parse(defaultValue || '{}') : defaultValue;
      return Array.isArray(parsed.items) ? parsed.items : [];
    } catch {
      return [];
    }
  }, [defaultValue]);
  const initialItems = useMemo(
    () => getItemsWithDefaultPositions(defaultItems, defaultBlocks ?? []),
    [defaultBlocks, defaultItems],
  );
  const [blocks, setBlocks] = useState(defaultBlocks);
  const [selectedBlockId, setSelectedBlockId] = useState<string>();
  const [selectedItemIndex, setSelectedItemIndex] = useState<number>();
  const [items, setItems] = useState<YardPlaceSceneItem[]>(initialItems);
  const [selectedEmptySlotKey, setSelectedEmptySlotKey] = useState<string>();
  const [hoveredSlotPointer, setHoveredSlotPointer] = useState<{
    slotKey: string;
    x: number;
    y: number;
  }>();
  const [hoveredContainerPointer, setHoveredContainerPointer] = useState<{
    containerId: string;
    x: number;
    y: number;
  }>();
  const [selectedContainerIdState, setSelectedContainerIdState] = useState<string>();
  const isContainerSelectionControlled = onSelectedContainerChange !== undefined;
  const selectedContainerId = isContainerSelectionControlled
    ? selectedContainerIdProp
    : selectedContainerIdProp ?? selectedContainerIdState;
  const [movedContainers, setMovedContainers] = useState<SlotsState['containers']>();
  const autoTierAppliedRef = useRef(false);
  const defaultValueRef = useRef(
    typeof defaultValue === 'string' ? defaultValue : JSON.stringify(defaultValue),
  );
  const initialSlotsState = useInitialSlotsState(
    blocks ? { blocks, containers: defaultContainers } : defaultValue,
  );
  const slotsState = useMemo(
    () => ({
      ...initialSlotsState,
      containers: movedContainers ?? initialSlotsState.containers,
    }),
    [initialSlotsState, movedContainers],
  );
  const updateSelectedContainerId = (containerId?: string) => {
    if (!isContainerSelectionControlled) {
      setSelectedContainerIdState(containerId);
    }

    onSelectedContainerChange?.(containerId);
  };

  useEffect(() => {
    if (selectedContainerIdProp) {
      setSelectedEmptySlotKey(undefined);
    }
  }, [selectedContainerIdProp]);

  const isBlocksEditing = defaultMode === 'edit-blocks' && !!blocks;
  const hoveredSlotInfo = useMemo((): YardPlaceSceneSlotHoverInfo | undefined => {
    if (!hoveredSlotPointer) return undefined;

    const { slotKey, x, y } = hoveredSlotPointer;
    const { col, row } = parseSlotKey(slotKey);
    const containers = getContainersInSlot(slotsState, col, row);
    const topTier = containers.reduce((tier, container) => Math.max(tier, container.tier), 0);

    return {
      slotKey,
      place: getSlotDisplayName(slotsState, slotKey),
      tier: topTier + 1,
      x,
      y,
    };
  }, [hoveredSlotPointer, slotsState]);
  const hoveredContainerInfo = useMemo((): YardPlaceSceneContainerHoverInfo | undefined => {
    if (!hoveredContainerPointer) return undefined;

    const container = slotsState.containers.find(
      (item) => item.id === hoveredContainerPointer.containerId,
    );
    if (!container) return undefined;

    return {
      containerId: container.id,
      number: container.number,
      type: container.type,
      clientCode: container.client?.code,
      place: getSlotDisplayName(slotsState, container.slotKey),
      tier: container.tier,
      x: hoveredContainerPointer.x,
      y: hoveredContainerPointer.y,
    };
  }, [hoveredContainerPointer, slotsState]);
  const selectedBlock = blocks?.find(
    (block, index) => `${block.name}-${index}` === selectedBlockId,
  );
  const selectedBlockRoads = selectedBlock?.roads ?? [];

  useEffect(() => {
    const nextDefaultValue =
      typeof defaultValue === 'string' ? defaultValue : JSON.stringify(defaultValue);

    if (defaultValueRef.current === nextDefaultValue) return;

    defaultValueRef.current = nextDefaultValue;
    setBlocks(defaultBlocks);
    setSelectedBlockId(undefined);
    setSelectedItemIndex(undefined);
    setItems(initialItems);
    setSelectedEmptySlotKey(undefined);
    setSelectedContainerIdState(undefined);
    onSelectedContainerChange?.(undefined);
    setMovedContainers(undefined);
    autoTierAppliedRef.current = false;
  }, [defaultBlocks, defaultItems, defaultValue, initialItems]);

  useEffect(() => {
    if (
      defaultMode === 'edit-blocks' ||
      !autoTier ||
      autoTierAppliedRef.current ||
      !blocks?.length
    ) {
      return;
    }

    autoTierAppliedRef.current = true;
    const normalizedContainers = normalizeContainerTiers(initialSlotsState.containers);

    setMovedContainers(normalizedContainers);
    onChange?.(
      getSceneDataValue(blocks, normalizedContainers, defaultItems, initialSlotsState.blockMaps),
    );
  }, [
    autoTier,
    blocks,
    defaultItems,
    defaultMode,
    initialSlotsState.blockMaps,
    initialSlotsState.containers,
    onChange,
  ]);

  useEffect(() => {
    if (isBlocksEditing) {
      onChange?.(
        JSON.stringify({
          blocks: blocks ?? [],
          containers: defaultContainers,
          items,
        }),
      );
    }
  }, [blocks, defaultContainers, isBlocksEditing, items, onChange]);

  const handleRotateBlock = (degrees: number) => {
    if (selectedItemIndex !== undefined) {
      setItems((currentItems) =>
        currentItems.map((item, index) =>
          index === selectedItemIndex ? { ...item, rotate: (item.rotate ?? 0) + degrees } : item,
        ),
      );
      return;
    }

    if (!selectedBlockId) return;

    setBlocks((currentBlocks) =>
      currentBlocks?.map((block, index) => {
        const blockId = `${block.name}-${index}`;
        if (blockId !== selectedBlockId) return block;

        return {
          ...block,
          rotate: (block.rotate ?? 0) + degrees,
        };
      }),
    );
  };

  const handleToggleRoad = (road: NonNullable<YardPlaceSceneBlock['roads']>[number]) => {
    if (!selectedBlockId) return;

    setBlocks((currentBlocks) =>
      currentBlocks?.map((block, index) => {
        if (`${block.name}-${index}` !== selectedBlockId) return block;

        const roads = block.roads ?? [];
        return {
          ...block,
          roads: roads.includes(road) ? roads.filter((item) => item !== road) : [...roads, road],
        };
      }),
    );
  };

  const handleMoveBlock = (x: number, y: number) => {
    if (selectedItemIndex !== undefined) {
      setItems((currentItems) =>
        currentItems.map((item, index) => {
          if (index !== selectedItemIndex) return item;

          const position = item.position ?? { x: 0, y: 0 };
          return {
            ...item,
            position: {
              x: Math.round((position.x + x) * 100) / 100,
              y: Math.round((position.y + y) * 100) / 100,
            },
          };
        }),
      );
      return;
    }

    if (!selectedBlockId || (!x && !y)) return;

    setBlocks((currentBlocks) =>
      currentBlocks?.map((block, index) => {
        if (`${block.name}-${index}` !== selectedBlockId) return block;

        const position = getBlockPosition(currentBlocks, index);
        return {
          ...block,
          position: {
            x: Math.round((position.x + x) * 100) / 100,
            y: Math.round((position.y + y) * 100) / 100,
          },
        };
      }),
    );
  };

  const handleMoveContainer = (containerId: string, targetSlotKey: string) => {
    const container = slotsState.containers.find((item) => item.id === containerId);
    if (!container) return;

    const sourceSlotKeys = container.slotKeys ?? [container.slotKey];
    const hasContainersAbove = slotsState.containers.some((item) => {
      if (item.id === containerId || item.tier <= container.tier || item.length !== 40)
        return false;

      const itemSlotKeys = item.slotKeys ?? [item.slotKey];
      return itemSlotKeys.some((slotKey) => sourceSlotKeys.includes(slotKey));
    });

    if (hasContainersAbove) {
      enqueueSnackbar(YARD_PLACE_SCENE_MESSAGES.twoBottomSlots, 'warning');
      return;
    }

    const sourceAnchor = parseSlotKey(sourceSlotKeys[0]);
    const targetAnchor = parseSlotKey(targetSlotKey);
    const targetSlotKeys = sourceSlotKeys.map((slotKey) => {
      const source = parseSlotKey(slotKey);
      return getSlotKey(
        targetAnchor.col + source.col - sourceAnchor.col,
        targetAnchor.row + source.row - sourceAnchor.row,
      );
    });

    if (targetSlotKeys.some((slotKey) => !slotsState.slotNames?.[slotKey])) {
      enqueueSnackbar(YARD_PLACE_SCENE_MESSAGES.notEnoughSlots, 'warning');
      return;
    }

    const occupiedTiers = targetSlotKeys.map((slotKey) =>
      slotsState.containers
        .filter(
          (item) =>
            item.id !== containerId &&
            (item.slotKeys?.includes(slotKey) ?? item.slotKey === slotKey),
        )
        .reduce((maxTier, item) => Math.max(maxTier, item.tier), 0),
    );

    if (!occupiedTiers.every((tier) => tier === occupiedTiers[0])) {
      enqueueSnackbar(YARD_PLACE_SCENE_MESSAGES.twoBottomSlots, 'warning');
      return;
    }

    if (!window.confirm('Save 3D Changes?')) {
      return;
    }

    const tier = occupiedTiers[0] + 1;
    const keyPart = slotKeyToContainerKeyPart(targetSlotKeys[0], initialSlotsState.blockMaps ?? []);
    const placeMatch = keyPart ? /^(.+)-(\d+)-(\d+)$/.exec(keyPart) : null;

    const movedContainer = {
      ...container,
      slotKey: targetSlotKeys[0],
      slotKeys: targetSlotKeys,
      tier,
    };
    setMovedContainers((currentContainers) =>
      normalizeContainerTiers(
        (currentContainers ?? slotsState.containers).map((item) =>
          item.id === containerId ? movedContainer : item,
        ),
      ),
    );

    if (placeMatch) {
      onPasteContainer?.(
        containerId,
        {
          block: placeMatch[1],
          row: Number(placeMatch[2]),
          slot: Number(placeMatch[3]),
          tier,
        },
        () => {
          setMovedContainers((currentContainers) => {
            const containers = currentContainers ?? slotsState.containers;
            const currentContainer = containers.find((item) => item.id === containerId);
            if (currentContainer?.slotKey !== movedContainer.slotKey) return containers;

            return normalizeContainerTiers(
              containers.map((item) => (item.id === containerId ? container : item)),
            );
          });
        },
      );
    }
  };

  const handleAddContainer = (container: AddedContainer) => {
    if (!selectedEmptySlotKey) return;

    const containers = movedContainers ?? slotsState.containers;
    const { col, row } = parseSlotKey(selectedEmptySlotKey);
    const slotKeys = [selectedEmptySlotKey];

    if (container.length === 40) {
      const selectedSlotTier = containers
        .filter(
          (item) =>
            item.slotKeys?.includes(selectedEmptySlotKey) ?? item.slotKey === selectedEmptySlotKey,
        )
        .reduce((maxTier, item) => Math.max(maxTier, item.tier), 0);
      const adjacentSlotKey = [getSlotKey(col, row + 1), getSlotKey(col, row - 1)].find(
        (slotKey) =>
          !!slotsState.slotNames?.[slotKey] &&
          containers
            .filter((item) => item.slotKeys?.includes(slotKey) ?? item.slotKey === slotKey)
            .reduce((maxTier, item) => Math.max(maxTier, item.tier), 0) === selectedSlotTier,
      );

      if (!adjacentSlotKey) {
        enqueueSnackbar(YARD_PLACE_SCENE_MESSAGES.notEnoughSlots, 'warning');
        return;
      }

      slotKeys.push(adjacentSlotKey);
    }

    const tier =
      Math.max(
        0,
        ...slotKeys.map((slotKey) =>
          containers
            .filter((item) => item.slotKeys?.includes(slotKey) ?? item.slotKey === slotKey)
            .reduce((maxTier, item) => Math.max(maxTier, item.tier), 0),
        ),
      ) + 1;

    setMovedContainers([
      ...containers,
      {
        ...container,
        slotKey: slotKeys[0],
        slotKeys,
        tier,
      },
    ]);
    setSelectedEmptySlotKey(undefined);

    const keyPart = slotKeyToContainerKeyPart(slotKeys[0], initialSlotsState.blockMaps ?? []);
    const placeMatch = keyPart ? /^(.+)-(\d+)-(\d+)$/.exec(keyPart) : null;

    if (placeMatch) {
      onPasteContainer?.(
        container.id,
        {
          block: placeMatch[1],
          row: Number(placeMatch[2]),
          slot: Number(placeMatch[3]),
          tier,
        },
        () => {
          setMovedContainers((currentContainers) =>
            (currentContainers ?? slotsState.containers).filter((item) => item.id !== container.id),
          );
        },
      );
    }
  };

  const handleDeleteContainer = () => {
    if (!selectedContainerId) return;

    const container = slotsState.containers.find((item) => item.id === selectedContainerId);
    if (!container) return;

    const sourceSlotKeys = container.slotKeys ?? [container.slotKey];
    const hasContainersAbove = slotsState.containers.some((item) => {
      if (item.id === selectedContainerId || item.tier <= container.tier) return false;

      return (item.slotKeys ?? [item.slotKey]).some((slotKey) => sourceSlotKeys.includes(slotKey));
    });

    if (hasContainersAbove) {
      enqueueSnackbar(YARD_PLACE_SCENE_MESSAGES.twoBottomSlots, 'warning');
      return;
    }

    if (!window.confirm('Save 3D Changes?')) {
      return;
    }

    setMovedContainers((currentContainers) =>
      (currentContainers ?? slotsState.containers).filter(
        (item) => item.id !== selectedContainerId,
      ),
    );
    updateSelectedContainerId(undefined);

    onDeleteContainer?.(selectedContainerId, () => {
      setMovedContainers((currentContainers) => {
        const containers = currentContainers ?? slotsState.containers;
        if (containers.some((item) => item.id === container.id)) return containers;

        return normalizeContainerTiers([...containers, container]);
      });
    });
  };

  const handleDeleteSelectedItem = () => {
    if (selectedItemIndex === undefined) return;

    setItems((currentItems) => currentItems.filter((_, index) => index !== selectedItemIndex));
    setSelectedItemIndex(undefined);
  };

  const handleAddItem = (type: YardPlaceSceneItem['type']) => {
    const nextItems = [
      ...items,
      {
        type,
        position: getSceneCenterPosition(blocks ?? []),
      },
    ];
    setItems(nextItems);
    setSelectedItemIndex(nextItems.length - 1);
    setSelectedBlockId(undefined);
  };

  const handleSelectBlock = (blockId?: string) => {
    if (blockId === selectedBlockId) return;

    setSelectedBlockId(blockId);
    setSelectedItemIndex(undefined);
  };

  return (
    <Layout>
      {isBlocksEditing && (
        <AddItemsPaper paperSize="max-content">
          <IconButton
            aria-label={'Add More'}
            iconColor="--primary-100"
            onClick={() => handleAddItem('railroad-tracks')}
          >
            <TrainIcon />
          </IconButton>
        </AddItemsPaper>
      )}
      {isBlocksEditing && selectedBlockId && (
        <RoadCheckboxesPaper paperSize="max-content">
          {(['top', 'right', 'bottom', 'left'] as const).map((road) => (
            <RoadCheckbox
              key={road}
              checked={selectedBlockRoads.includes(road)}
              icon={
                road === 'top' ? (
                  <ArrowUpwardIcon />
                ) : road === 'right' ? (
                  <ArrowForwardIcon />
                ) : road === 'bottom' ? (
                  <ArrowDownwardIcon />
                ) : (
                  <ArrowBackIcon />
                )
              }
              checkedIcon={
                road === 'top' ? (
                  <ArrowUpwardIcon />
                ) : road === 'right' ? (
                  <ArrowForwardIcon />
                ) : road === 'bottom' ? (
                  <ArrowDownwardIcon />
                ) : (
                  <ArrowBackIcon />
                )
              }
              onChange={() => handleToggleRoad(road)}
            />
          ))}
        </RoadCheckboxesPaper>
      )}
      {isBlocksEditing && (
        <SceneControls>
          <BlockRotateControl
            disabled={!selectedBlockId && selectedItemIndex === undefined}
            onRotate={handleRotateBlock}
          />
          <BlockPositionControl
            disabled={!selectedBlockId && selectedItemIndex === undefined}
            onMove={handleMoveBlock}
          />
          {selectedItemIndex !== undefined && (
            <DeleteItemControl
              aria-label={'Delete'}
              iconColor="--white"
              onClick={handleDeleteSelectedItem}
            >
              <DeleteOutlineIcon />
            </DeleteItemControl>
          )}
        </SceneControls>
      )}
      <View3D
        state={isBlocksEditing ? { ...slotsState, containers: [] } : slotsState}
        selectedContainerNumber={defaultSelected}
        selectedContainerFocusKey={selectedContainerFocusKey}
        slotColor={slotColor}
        slotContainerColor={slotContainerColor}
        containerSelectedColor={containerSelectedColor}
        selectedBlockId={selectedBlockId}
        onSelectBlock={isBlocksEditing ? handleSelectBlock : undefined}
        isBlocksEditing={isBlocksEditing}
        isContainersEditing={defaultMode === 'edit-containers'}
        onMoveContainer={handleMoveContainer}
        onEmptySlotSelect={(slotKey) => {
          updateSelectedContainerId(undefined);
          setSelectedEmptySlotKey(slotKey);
        }}
        selectedEmptySlotKey={selectedEmptySlotKey}
        items={items}
        selectedItemIndex={isBlocksEditing ? selectedItemIndex : undefined}
        onSelectItem={
          isBlocksEditing
            ? (itemIndex) => {
                setSelectedBlockId(undefined);
                setSelectedItemIndex(itemIndex);
              }
            : undefined
        }
        onContainerClick={(containerId) => {
          setSelectedEmptySlotKey(undefined);
          updateSelectedContainerId(containerId);
        }}
        backgroundColor={backgroundColor}
        onHoverSlotChange={setHoveredSlotPointer}
        onHoverContainerChange={setHoveredContainerPointer}
      />
      {renderOnContainerHover?.(hoveredContainerInfo)}
      {renderOnSlotHover?.(hoveredSlotInfo)}
      {(() => {
        if (selectedEmptySlotKey) {
          return renderOnEmptyClick?.(
            selectedEmptySlotKey,
            () => setSelectedEmptySlotKey(undefined),
            handleAddContainer,
            slotsState.containers,
          );
        }

        const containerPanel =
          selectedContainerId &&
          renderOnContainerClick?.(
            selectedContainerId,
            () => updateSelectedContainerId(undefined),
            handleDeleteContainer,
          );

        if (containerPanel) return containerPanel;

        return renderOnEmptyClick?.(
          selectedEmptySlotKey,
          () => setSelectedEmptySlotKey(undefined),
          handleAddContainer,
          slotsState.containers,
        );
      })()}
    </Layout>
  );
};
